import {
  eventsState,
  selectEventsSelected,
  selectRegularEventsSelected,
  selectSpecialEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { useAppStore } from "@/global/store";
import {
  CartEvent,
  CartManagerState,
  cartManagerState,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import {
  findUnholdEventsInCart,
  findEventInCart,
  findLocationsHeldInCart,
  findNextUnholdEvent,
} from "./utils.cart";
import { useFilterApis } from "../GameFilters/useFilterApis";
import { EventType } from "@/global/store/data/types";
import {
  purchaseSeatsTabUIDs as tabUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { useDiffSeatsModal } from "./ChooseSeats/diff-seats/DiffSeatsAlertModal";
import { toast } from "react-toastify";
import { parseApiErrorMessage } from "@/global/utils/utils";
import { serviceFeeState } from "@/global/store/data/purchaseSeats/serviceFee";
import { useManagePerkErrorModal } from "./modals/ManagePerkErrorModal";
import {
  isCartHasFailedPerks,
  cartHasFailedMandatoryPerks,
  findAllPerksLocations,
} from "./utils.perks";
import {
  paymentPlansState,
  selectPaymentPlans,
} from "@/global/store/data/purchaseSeats/paymentPlans";
import { useApiRoutes } from "@/global/hooks/useApiRoutes";
import { ManageCartPayload } from "@/types/api.types";
import { useEditSeatsTrack } from "@/global/hooks/useEditSeatsTrack";
import { useCancelCartWarningModal } from "./modals/CancelCartWarningModal";
import { useCartNavigationHelper } from "./useCartBackButtonHelper";
import { useNoInventoryAlertModal } from "./ChooseSeats/force-event-selection/NoInventoryAlertModal";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useSelectActiveEvent } from "./ChooseSeats/useSelectActiveEvent";
import {
  createCancelCartPayload,
  createAutoSeatSelectionPayload,
  retryApiRequest,
} from "@/global/api/api.utils";
import { useAutoSeatSelectionAlertModal } from "./ChooseSeats/auto-seat-selection/AutoSeatSelectionAlertModal";
import { useAutoSeatSelectionProgressModal } from "./ChooseSeats/auto-seat-selection/AutoSeatSelectionProgressModal";
import { selectSeatCount } from "@/global/store/data/gameFilters";
import { useDispatch } from "react-redux";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAutoSeatSelectionState } from "./ChooseSeats/auto-seat-selection/useAutoSeatSelectionState";
import { useAutoSeatSelectionNoInventoryAlertModal } from "./ChooseSeats/auto-seat-selection/AutoSeatSelectionNoInventoryAlertModal";
import { NextRouter } from "next/router";

export function useCartHelpers() {
  // system
  const router = useRouterExtended();
  const dispatch = useDispatch();
  const teamConfig = useTeamConfig();
  const { teamSchema } = teamConfig;

  // modals
  const diffSeatsModal = useDiffSeatsModal();
  const managePerkErrorModal = useManagePerkErrorModal();
  const cancelCartWarningModal = useCancelCartWarningModal();
  const noInventoryAlertModal = useNoInventoryAlertModal(); // prettier-ignore
  const autoSeatNoInvAlertModal = useAutoSeatSelectionNoInventoryAlertModal();
  const autoSeatSelectionAlertModal = useAutoSeatSelectionAlertModal();
  const autoSeatSelectionProgressModal = useAutoSeatSelectionProgressModal();

  // apis
  const { manageCartApi, cancelCartApi, autoSeatSelectionApi, managePerkApi } =
    useApiRoutes();

  // helpers
  const { backToFindGames } = useCartNavigationHelper();

  // states
  const filterSeatCount = useAppStore(selectSeatCount);
  const { activeEvent } = useSelectActiveEvent();
  const cartState = useAppStore(selectCartManager);
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedSpecialEvents = useAppStore(selectSpecialEventsSelected);
  const selectedRegularEvents = useAppStore(selectRegularEventsSelected);
  const paymentPlans = useAppStore(selectPaymentPlans);
  const autoSeatSelectionState = useAutoSeatSelectionState();
  const { set: setEditSeatsClicked } = useEditSeatsTrack();

  /*
  +------------------------------------------------------------------------------+
  helper utility for high level cart management functions
  +------------------------------------------------------------------------------+ */

  // basic function for switching purchase seat tab
  const switchPurchaseSeatTab = async (
    purchaseSeatTab: string,
    eventCode?: string
  ) => {
    const query = { ...router.query, purchaseSeatTab } as NextRouter["query"];
    if (eventCode) query.eventCode = eventCode;
    await router.replace({ query });
    window.scrollTo({ top: 0 });
  };

  // function for setting the active event in the url
  const setCartActiveEventUrl = async (
    event: EventType | null,
    shouldReplace?: boolean
  ) => {
    if (event === null) {
      delete router.query.eventCode;
      await router.replace({ query: { ...router.query } });
    } else {
      const query = { ...router.query, eventCode: event.eventCode };
      await router.replace({ query });
    }
  };

  // function for switching forward purchase seat tab.
  // currently handling tabUIDs.chooSeats and findGames tabs, can be extended for other tabs
  const forwardPurchaseSeatTab = () => {
    const currentTab = router.query.purchaseSeatTab as string;
    switch (currentTab) {
      case tabUIDs.chooseSeats:
        if (teamConfig.purchaseSeatConfigs?.isPerksEnabled) {
          return switchPurchaseSeatTab(tabUIDs.selectPerks);
        } else {
          return switchPurchaseSeatTab(tabUIDs.checkout);
        }

      case tabUIDs.findGames:
        if (teamConfig.purchaseSeatConfigs?.isPerksEnabled) {
          return switchPurchaseSeatTab(tabUIDs.selectPerks);
        } else {
          return switchPurchaseSeatTab(tabUIDs.checkout);
        }
    }
  };

  const removeCartProductsFromCartState = (
    cartEvent: CartEvent | EventType | null
  ) => {
    if (!cartEvent) return;
    dispatch(
      cartManagerState.actions.removeProducts({
        eventCode: cartEvent?.eventCode,
      })
    );
  };

  const removeCartEventFromCartState = (eventCode?: string) => {
    if (!eventCode) return;
    dispatch(
      cartManagerState.actions.removeEvent({
        eventCode,
      })
    );
  };

  const replaceEventInSelectedEvents = (
    eventToReplace: EventType | CartEvent | null,
    newSelectedEvents: EventType[]
  ) => {
    if (!eventToReplace) return;
    const updatedSelectedEvents = selectedEvents.map((event) => {
      if (event.eventCode === eventToReplace.eventCode) {
        return newSelectedEvents[0];
      }
      return event;
    });
    const restNewSelection = newSelectedEvents.slice(1);
    updatedSelectedEvents.push(...restNewSelection);
    dispatch(
      eventsState.actions.setEventsSelectedWithoutSort(updatedSelectedEvents)
    );
  };

  // cancel cart api call
  const callCancelCartApi = async (args: {
    seatLocations?: string[];
    perkLocations?: string[];
    cartState?: CartManagerState;
    offerId?: number;
  }) => {
    const { seatLocations, perkLocations, offerId } = args;
    let payload = createCancelCartPayload({
      teamConfig,
      cartState: args?.cartState || cartState,
      seatLocations,
      perkLocations,
      offerId,
    });
    let { data: response, error } = await cancelCartApi.post(payload);
    if (error) return console.log(parseApiErrorMessage(error));

    if (teamConfig.platform == vendorUUIDs.tickesdotcom && response.sessionId) {
      // update the session id in redux
      dispatch(
        cartManagerState.actions.setSessionId({
          sessionId: response.sessionId,
        })
      );
    }

    dispatch(
      cartManagerState.actions.removeLocationHeld({
        locations: args.seatLocations || [],
        perkLocations: args.perkLocations || [],
      })
    );
    dispatch(serviceFeeState.actions.removeResponse());

    return response;
  };

  // manage cart api call
  const callManageCartApi = async (cartState: CartManagerState) => {
    let { cart, sessionId } = { ...cartState };
    let payload: ManageCartPayload = { teamSchema, cart, sessionId };
    if (cartState.cartId) payload.cartId = cartState.cartId;
    if (teamConfig.platform == vendorUUIDs.tickesdotcom) {
      setEditSeatsClicked({ isEditSeatsClicked: false });
    }
    return await manageCartApi.post(payload);
  };

  // hold similar seats api call
  const callAutoSeatSelectionApi = async (args: {
    cartState: CartManagerState;
    selectedEvents: EventType[];
    cartEventActive: CartEvent;
  }) => {
    const payload = createAutoSeatSelectionPayload({
      teamConfig,
      selectedEvents: args.selectedEvents,
      cartState: args.cartState,
      filterSeatCount,
    });

    const fetcher = async (payload: any) => {
      const { response } = await autoSeatSelectionApi.post(payload);
      return response;
    };
    return await retryApiRequest(fetcher, {
      payload,
      status: 202,
      delay: 2000,
      nextReqDataKey: "jobId",
      retryLimit: 15,
    });
  };

  const cancelCartByEvent = async (args: {
    cartEvent: CartEvent | null;
    cartState?: CartManagerState;
  }) => {
    if (!args.cartEvent) return;
    if (args.cartEvent?.isHeld && args.cartEvent?.location) {
      await callCancelCartApi({
        seatLocations: [args.cartEvent.location],
        offerId: args?.cartEvent?.offerId,
        cartState: args?.cartState || cartState,
      });
    }
  };

  // function for resetting cart selections
  const resetCartSelections = async (replaceQuery = true) => {
    // preventing clearing event selection if the pkg confirm modal is open
    if (router?.query?.isPkgConfirmOpen) return;

    // clearing the event selection
    dispatch(eventsState.actions.clearEventsSelected());

    // cancelling cart
    let heldLocations = findLocationsHeldInCart(cartState);
    const perkLocations = findAllPerksLocations(cartState);
    if (heldLocations.length > 0 || perkLocations.length > 0) {
      await callCancelCartApi({
        seatLocations: heldLocations as string[],
        perkLocations,
      });
    }

    // clearing cart data in redux
    dispatch(cartManagerState.actions.set({ cart: [] }));

    // resetting active event
    if (activeEvent && replaceQuery) setCartActiveEventUrl(null);

    // resetting auto seat selection mode for the last cart session
    autoSeatSelectionState.reset();

    // reseting payment plans
    if (paymentPlans.length > 0) {
      dispatch(serviceFeeState.actions.removeResponse());
      dispatch(paymentPlansState.actions.set([]));
    }
  };

  const handleCartCancelResponse = async () => {
    await cancelCartWarningModal.execute({ isOpen: true });
    return await backToFindGames();
  };

  // ----------------------------------------------------------------------------
  // handling manage cart no inventory event
  // ----------------------------------------------------------------------------
  const handleNoInventoryEvent = async (args: {
    event: CartEvent | EventType | null;
    isInventoryApiTriggered?: boolean;
  }) => {
    // pre-checking if the event code is available
    if (!args.event || !args.event?.eventCode) return;

    // if inventory is not available, then firing up the no inventory message modal
    const eventCode = args.event?.eventCode as string;
    let modalResponse = await noInventoryAlertModal.execute({
      eventCode,
      isInventoryApiTriggered: args?.isInventoryApiTriggered || false,
    });

    // If user confirmed game selection with new events
    // then updating the latest selected events and active event
    if (modalResponse.action == "confirmedGameSelection") {
      const modalSelectedEvents = modalResponse?.selectedEvents;
      if (!modalSelectedEvents?.length) return;
      replaceEventInSelectedEvents(activeEvent, modalSelectedEvents);
      removeCartEventFromCartState(activeEvent?.eventCode);
      await setCartActiveEventUrl(modalSelectedEvents[0], true);
    }

    // If user continues without selecting any events
    if (modalResponse.action == "continue") {
      const nextUnHoldEvent = findNextUnholdEvent(
        activeEvent,
        selectedEvents,
        cartState
      );
      dispatch(eventsState.actions.removeEvent(activeEvent?.eventCode || ""));
      removeCartEventFromCartState(activeEvent?.eventCode);
      removeCartProductsFromCartState(nextUnHoldEvent);
      if (!nextUnHoldEvent) await forwardPurchaseSeatTab();
      else await setCartActiveEventUrl(nextUnHoldEvent, true);
    }

    // if user wants to try again
    if (modalResponse.action == "tryAgain") {
      removeCartEventFromCartState(activeEvent?.eventCode);
      return;
    }

    // If user wants to go back to find games
    if (modalResponse.action == "backToFindGames") {
      backToFindGames();
    }

    return modalResponse;
  };

  // ----------------------------------------------------------------------------
  // handling manage cart diff seat
  // ----------------------------------------------------------------------------
  const handleManageCartDiffSeat = async (cartState: CartManagerState) => {
    // pre-checking if the active event is available in the cart
    const cartEventActive = findEventInCart(activeEvent, cartState?.cart);
    if (!cartEventActive) return;

    // if the active event has seats different from the user's selected seats, then firing up the diff seats modal
    let modalResponse = await diffSeatsModal.execute({ cartEventActive });

    // user wants to try another seat
    if (modalResponse.action == "tryAnotherSeat") {
      await cancelCartByEvent({ cartEvent: cartEventActive, cartState });
      removeCartProductsFromCartState(cartEventActive);
      if (selectedEvents.length == 1) {
        // clearing cart data in redux if this is the only event in the cart
        dispatch(cartManagerState.actions.set({ cart: [] }));
      }
    }

    // user wants to accept suggested seat
    if (modalResponse.action == "acceptSuggestedSeat") {
      const nextUnHoldEvent = findNextUnholdEvent(
        activeEvent,
        selectedEvents,
        cartState
      );
      if (!nextUnHoldEvent) await forwardPurchaseSeatTab();
      else await setCartActiveEventUrl(nextUnHoldEvent);
    }
    return modalResponse;
  };

  // ----------------------------------------------------------------------------
  // proceed further in manage cart after handling no inventory event and diff seats
  // ----------------------------------------------------------------------------
  const handleManageCartProceed = async (args: {
    cartState: CartManagerState;
    onSeatHoldFailed?: (a?: { errorMessage?: string }) => void;
  }) => {
    const cartEventActive = findEventInCart(activeEvent, args.cartState.cart);
    if (!cartEventActive) return;

    // checking if the active event has an error
    const isErrorEvent = !!cartEventActive?.errorMessage;
    // checking if the active event is held
    const isHeldEvent = cartEventActive?.isHeld && cartEventActive?.location;

    // if error event, then removing inventory selection
    if (isErrorEvent) {
      await args?.onSeatHoldFailed?.({ errorMessage: cartEventActive?.errorMessage }); // prettier-ignore
      return removeCartProductsFromCartState(cartEventActive);
    }
    // finding next unhold event
    const nextUnholdEvent = findNextUnholdEvent(activeEvent, selectedEvents, args.cartState); // prettier-ignore
    // if current event is held and next event is available, setting it as active event
    if (isHeldEvent && nextUnholdEvent) {
      removeCartProductsFromCartState(nextUnholdEvent);
      return await setCartActiveEventUrl(nextUnholdEvent);
    }
    // everything is fine, proceed tab to next tab
    return await forwardPurchaseSeatTab();
  };

  // ----------------------------------------------------------------------------
  // handling regular manage cart
  // ----------------------------------------------------------------------------
  const handleManageCart = async (args?: {
    onSeatHoldFailed?: (a?: { errorMessage?: string }) => void;
  }) => {
    const { data: manageCartRes, error } = await callManageCartApi(cartState);
    if (error) return toast.error(parseApiErrorMessage(error));

    const cartEventActive = findEventInCart(activeEvent, manageCartRes.cart);
    const retryCount = cartEventActive?.retryCount || 0;
    const hasNoInventory = cartEventActive?.hasNoInventory;

    // setting the cart state in redux
    dispatch(cartManagerState.actions.set(manageCartRes));

    // checking if the cart has cancelCart true
    if (manageCartRes.cancelCart) {
      return await handleCartCancelResponse();
    }

    // checking if the cart event has no inventory available
    if (hasNoInventory || (retryCount >= 0 && !cartEventActive?.isHeld)) {
      await args?.onSeatHoldFailed?.();
      return await handleNoInventoryEvent({ event: cartEventActive });
    }

    // handling retry seat selection if cart event has diff seats
    if (retryCount > 0 && cartEventActive?.isHeld) {
      await args?.onSeatHoldFailed?.();
      return await handleManageCartDiffSeat(manageCartRes);
    }

    // everything is fine, proceed further
    return await handleManageCartProceed({
      cartState: manageCartRes,
      onSeatHoldFailed: args?.onSeatHoldFailed,
    });
  };

  // ----------------------------------------------------------------------------
  // handling auto seat selection no inventory events
  // ----------------------------------------------------------------------------
  const handleAutoSeatsNoInventory = async (args: {
    cartEventActive: CartEvent | null;
    unHoldEvents: EventType[];
  }) => {
    const modalRes = await autoSeatNoInvAlertModal.execute({
      eventCodes: args.unHoldEvents.map((evt) => evt.eventCode),
    });

    if (modalRes.action == "manualSelect") {
      setCartActiveEventUrl(args.unHoldEvents[0]);
      removeCartProductsFromCartState(args.unHoldEvents[0]);
      return;
    }
  };

  // ----------------------------------------------------------------------------
  // handling auto seat selection diff seats
  // ----------------------------------------------------------------------------
  const handleAutoSelectionDiffSeats = async (args: {
    cartEventActive: CartEvent | null;
    cartState: CartManagerState;
  }) => {
    const { cartEventActive, cartState } = args;
    if (!cartEventActive) return;
    // if the active event has seats different from the user's selected seats, then firing up the diff seats modal
    let modalResponse = await diffSeatsModal.execute({ cartEventActive });

    // user wants to try another seat
    if (modalResponse.action == "tryAnotherSeat") {
      await cancelCartByEvent({ cartEvent: cartEventActive, cartState });
      removeCartProductsFromCartState(cartEventActive);
      if (selectedEvents.length == 1) {
        // clearing cart data in redux if this is the only event in the cart
        dispatch(cartManagerState.actions.set({ cart: [] }));
      }
    }

    if (modalResponse.action == "acceptSuggestedSeat") {
      await handleAutoSelectionProceed({
        cartEventActive,
        cartState,
      });
    }

    return modalResponse;
  };

  // -----------------------------------------------------------------------------------------
  // Proceed further in auto seat selection after handling no inventory events and diff seats
  // -----------------------------------------------------------------------------------------
  const handleAutoSelectionProceed = async (args: {
    cartEventActive: CartEvent | null;
    cartState: CartManagerState;
  }) => {
    const { cartEventActive, cartState } = args;
    if (!cartEventActive) return;

    autoSeatSelectionProgressModal.set({ isOpen: true });
    const { data: cartRes, error } = await callAutoSeatSelectionApi({
      cartState,
      cartEventActive,
      selectedEvents: selectedEvents?.filter(
        (evt) => evt?.eventCode !== cartEventActive?.eventCode
      ),
    });
    autoSeatSelectionProgressModal.set({ isOpen: false });

    if (error) return toast.error(parseApiErrorMessage(error));

    if (cartRes?.cancelCart) return await handleCartCancelResponse();
    dispatch(cartManagerState.actions.set(cartRes));

    const unHoldEvents = findUnholdEventsInCart(selectedEvents, cartRes);
    if (unHoldEvents.length > 0) {
      return await handleAutoSeatsNoInventory({
        unHoldEvents,
        cartEventActive,
      });
    }

    // once it's done, setting the modal to manual seat selection mode
    // this is to prevent the auto seat selection again in the same session
    autoSeatSelectionState.set({ mode: "manualSelect" });

    // forwarding to the next tab
    await forwardPurchaseSeatTab();
  };

  // ----------------------------------------------------------------------------
  // handling auto seat selection
  // ----------------------------------------------------------------------------
  const handleAutoSeatSelection = async () => {
    // if there is only one event, then returning with manual manage cart
    if (selectedEvents.length === 1) return await handleManageCart();

    const alertModalRes = await autoSeatSelectionAlertModal.execute();

    if (alertModalRes?.action == "autoSelect") {
      autoSeatSelectionProgressModal.set({ isOpen: true });
      const { data: manageCartRes, error } = await callManageCartApi(cartState);
      autoSeatSelectionProgressModal.set({ isOpen: false });
      if (error) return toast.error(parseApiErrorMessage(error));

      dispatch(cartManagerState.actions.set(manageCartRes));

      const cartEventActive = findEventInCart(activeEvent, manageCartRes.cart);

      const retryCount = cartEventActive?.retryCount as number;
      const isHeld = cartEventActive?.isHeld;
      const hasNoInventory = cartEventActive?.hasNoInventory;

      // handling no inventory
      if (hasNoInventory || (retryCount > 0 && !isHeld)) {
        return await handleNoInventoryEvent({ event: cartEventActive });
      }

      // handling diff seats
      if (retryCount > 0 && isHeld) {
        return await handleAutoSelectionDiffSeats({
          cartEventActive,
          cartState: manageCartRes,
        });
      }

      // everything is fine, call hold similar seats api here
      await handleAutoSelectionProceed({
        cartEventActive,
        cartState: manageCartRes,
      });
    }

    if (alertModalRes.action == "manualSelect") {
      return await handleManageCart();
    }

    if (alertModalRes.action == "changeInitialSelection") {
      return;
    }
  };

  // ----------------------------------------------------------------------------
  // handling perk selection confirmation
  // ----------------------------------------------------------------------------
  const onConfirmPerkSelection = async () => {
    if (cartState.sessionId) {
      const { data: managePerkRes, error } = await managePerkApi.post({
        teamSchema,
        sessionId: cartState?.sessionId,
        cartId: cartState?.cartId,
        cart: cartState.cart,
        cartTotalAmount: cartState?.allInPrice, // tdc
        orderServiceFee: cartState?.orderServiceFee, // tdc
        allInPrice: cartState?.allInPrice, // tdc
        basePrice: cartState?.basePrice, // tdc
        serviceFee: cartState?.serviceFee, // tdc
      });
      if (error) return toast.error(parseApiErrorMessage(error));
      if (managePerkRes) dispatch(cartManagerState.actions.set(managePerkRes)); // prettier-ignore
      const isFailedMandatoryPerks = cartHasFailedMandatoryPerks(managePerkRes); // prettier-ignore
      const isFailedPerks = isCartHasFailedPerks(managePerkRes);
      if (isFailedPerks) return managePerkErrorModal.set({ isOpen: true, isFailedMandatoryPerks }); // prettier-ignore
      return await switchPurchaseSeatTab(tabUIDs.checkout);
    }
  };

  return {
    handlers: {
      resetCartSelections,
      handleManageCart,
      cancelCartByEvent,
      removeCartProductsFromCartState,
      switchPurchaseSeatTab,
      onConfirmPerkSelection,
      forwardPurchaseSeatTab,
      setCartActiveEventUrl,
    },

    // started global exports with all members
    resetCartSelections,
    handleManageCart,
    handleAutoSeatSelection,
    callCancelCartApi,
    cancelCartByEvent,
    removeCartProductsFromCartState,
    removeCartEventFromCartState,
    switchPurchaseSeatTab,
    onConfirmPerkSelection,
    forwardPurchaseSeatTab,
    setCartActiveEventUrl,
    handleNoInventoryEvent,
  };
}
